import { reportingDeviceListQuerySchema, type DeviceMapping } from '@chukua/common';
import { Select } from 'antd';
import { useCallback, useEffect } from 'react';
import { useRemoteSearchOptions } from '../../../../../shared/hooks/use-remote-search-options';
import { listReportingDevices } from '../gps.api';
import { deviceOptionLabel, encodeDeviceChoice } from '../policies/device-choice.policy';

const PAGE_SIZE = 20;

interface ReportingDeviceSelectProps {
  id?: string;
  /** `<integration id>:<device id>`, from `encodeDeviceChoice`. */
  value?: string;
  onChange?: (value: string | undefined) => void;
  /** The truck being edited, so the device it carries is still offered; a new truck has none. */
  truckId?: string;
  /** The device the truck carries now, so its label shows even when it has not reported lately enough to be on the first page. */
  current?: DeviceMapping | null;
}

/**
 * Picker over the devices a GPS installation has heard from and no truck carries (gitaumoses4/chukua#422): `GET /tracking/reporting-devices?q=`,
 * opening on the newest-heard page. There is no typed fallback — a device nobody has heard from cannot be put on a truck from the form.
 */
export function ReportingDeviceSelect({ id, value, onChange, truckId, current }: ReportingDeviceSelectProps) {
  const fetchDevices = useCallback(
    (q: string | undefined) =>
      listReportingDevices({ ...reportingDeviceListQuerySchema.parse({ q, pageSize: PAGE_SIZE }), forTruckId: truckId }).then((page) =>
        page.items.map((device) => ({ value: encodeDeviceChoice(device), label: deviceOptionLabel(device), title: `Last heard ${device.lastHeardAt}` })),
      ),
    [truckId],
  );
  const { pin, selectProps } = useRemoteSearchOptions(fetchDevices);

  useEffect(() => {
    if (current) pin({ value: encodeDeviceChoice(current), label: deviceOptionLabel(current), title: '' });
  }, [current, pin]);

  return (
    <Select
      {...selectProps}
      id={id}
      value={value}
      onChange={onChange}
      allowClear
      placeholder="Select or search GPS devices"
      notFoundContent={selectProps.loading ? 'Searching…' : 'No GPS device has reported yet. Install an integration under Integrations.'}
    />
  );
}
